import { axiosClient, getPostHogHeaders } from "auth-sdk";
import { addAction, addError } from "@wayflyer/blackbox-fe";
import { create, type AxiosInstance } from "axios";

import { clearCachedBasiqCredentials, getCachedBasiqCredentials, updateCachedBasiqCredentials } from "./basiq-storage";

type TokenGetterFunction = () => Promise<any>;

function createBasiqAxiosClient(tokenGetter: TokenGetterFunction) {
  const basiqClient = create({
    headers: {
      "Content-Type": "application/json",
    },
  });
  basiqClient.interceptors.request.use(async (config) => {
    const { token } = await tokenGetter();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  // Request interceptor to inject PostHog headers for internal Wayflyer API calls
  basiqClient.interceptors.request.use((config) => {
    // Only add PostHog headers for Wayflyer API calls, not external Basiq API calls
    if (config.url && !config.url.includes("basiq.io")) {
      const posthogHeaders = getPostHogHeaders();
      Object.assign(config.headers, posthogHeaders);
    }
    return config;
  });
  return basiqClient;
}

enum ConsentUIAction {
  ONBOARDING = "",
  MANAGE = "manage", // manage the consent they have granted for your application.
  CONNECT = "connect", // already has an existing valid consent, but would like to make additional connections.
}

export interface BasiqClientConfig {
  startAuthFlowPath: string;
  userId?: string;
}

export class BasiqClient {
  _basiqClient: AxiosInstance;
  _wayflyerClient: AxiosInstance;

  startAuthFlowPath: string;
  userId?: string;
  readyForConsentUI: boolean;
  consentInitError?: Error;
  consents: Array<any>;

  constructor({ startAuthFlowPath, userId }: BasiqClientConfig) {
    this.startAuthFlowPath = startAuthFlowPath;
    this.userId = userId;
    this.readyForConsentUI = false;
    this.consents = [];

    this._basiqClient = createBasiqAxiosClient(this.getClientToken.bind(this));
    this._wayflyerClient = axiosClient;
  }

  init() {
    return this.getClientToken();
  }

  async initForConsentUI() {
    try {
      await this.init();
      this.consents = await this.getUserConsent();
    } catch (error) {
      addError(error);
      this.consentInitError = error;
    }
    this.readyForConsentUI = true;
  }

  async getInstitution(institutionId: string) {
    const response = await this._basiqClient.get(`https://au-api.basiq.io/institutions/${institutionId}`);
    return response.data;
  }

  async getUserConsent() {
    try {
      const response = await this._basiqClient.get(`https://au-api.basiq.io/users/${this.userId}/consents`);
      return response.data.data;
    } catch (error) {
      if (error.response.status === 404) {
        return []; // user has no consent. Valid case
      }
      throw error;
    }
  }

  async getClientToken() {
    const { userId, token, expiresDate, state } = getCachedBasiqCredentials();

    // if we have no userId at all or the userId we have is not
    // the same as one that was stored previously, we should refetch
    const userOutOfDate = !this.userId || (this.userId && this.userId !== userId);

    if (!token || Date.now() > parseInt(`${expiresDate}`) || userOutOfDate) {
      // If we don't have a client token in memory or the token has expired, fetch a new one
      return await this.updateCredentials();
    }

    return { token, userId, state };
  }

  async updateCredentials() {
    const { token, userId, state } = await this.getNewClientToken();
    updateCachedBasiqCredentials(token, userId, state);
    this.userId = userId;
    return { token, userId, state };
  }

  async getNewClientToken() {
    const result = await this._wayflyerClient.post(this.startAuthFlowPath);
    return result.data;
  }

  async initiateReconnection(connectionId: string) {
    const result = await this._wayflyerClient.post(
      this.startAuthFlowPath + "&reconnect=true&connection_id=" + connectionId
    );
    return result.data;
  }

  private hasExistingConsent() {
    return this.consents && Array.isArray(this.consents) && this.consents.length > 0;
  }

  emitBasiqDataDogAction() {
    const attributes = {
      has_connections: this.hasExistingConsent(),
    };
    // eslint-disable-next-line no-console
    console.log("Basiq Open", attributes);
    addAction("Basiq Open", attributes);
  }

  async launchConsentUI() {
    const { token, userId, state } = await this.getClientToken();
    const action = this.hasExistingConsent() ? ConsentUIAction.CONNECT : ConsentUIAction.ONBOARDING;
    const url = `https://consent.basiq.io/home?userId=${userId}&token=${token}&action=${action}&state=${state}`;
    clearCachedBasiqCredentials(userId);
    window.location.href = url;
    this.emitBasiqDataDogAction();
  }

  async launchReconnectionUI(connectionId: string) {
    const { url, userId } = await this.initiateReconnection(connectionId);
    clearCachedBasiqCredentials(userId);
    window.location.href = url;
    this.emitBasiqDataDogAction();
  }
}
